import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ObjectSchema } from 'yup';
import { FormHelpers, OnFormInitialValuesChange } from '../../../../../screens/utils';
import { journeyDataActions } from '../../../../../services/redux/features/journeyData.slice';
import { FormSectionEditButton } from '../../../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { ContactPersonFormModel_OL } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';
import { formAutocomplete } from '../../../../../config';

type Props = {
  formHelpers: FormHelpers;
};

/**
 * - ContactPersonForm
 * - SoleTrader
 * - OperatingLease
 */
export const ContactPersonForm_ST_OL: React.FC<Props> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema] = useState<ObjectSchema<Partial<ContactPersonFormModel_OL>>>(getValidationSchema);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: ContactPersonFormModel_OL) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`contactPersonForm:title:business`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent onFormInitialValuesChange={onFormInitialValuesChange} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  formHelpers: FormHelpers;
  onFormInitialValuesChange: OnFormInitialValuesChange;
};

export const FormContent = ({ formHelpers }: FormContentProps) => {
  const [inputs] = useState(() => createFormInputs('contactPersonForm'));
  const dispatch = useDispatch();
  const { values, submitForm } = useFormikContext<ContactPersonFormModel_OL>();
  const { isDisabledFormSummary, onFormValuesChange } = formHelpers;

  useEffect(() => {
    dispatch(journeyDataActions.updateData(requestMapper(values)));
    onFormValuesChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, onFormValuesChange]);

  const disableForm = isDisabledFormSummary;

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.title()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.firstName()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.middleName()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.lastName()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.maritalStatus()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.netMonthlyIncome()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.incomeFrequency()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/6" s="1/1">{inputs.countryCode()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.phoneNumber()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.email()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>
    <FormSectionSubmitButton formHelpers={formHelpers} onClick={submitForm} />
    </Form>
  );
};
